import { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import {
  Maybe,
  Portfolio,
  PortfolioHolding,
  PortfolioSecurityAllocation,
  SelfDirectedPortfolio,
} from 'generated/types'
import { CRYPTO_GRADIENT } from 'pages/portfolio/Crypto'
import { getInvestmentCategoryPercentages } from 'pages/portfolio/helperFunctions'
import { PortfolioType } from 'pages/portfolio/types'
import themeValues from 'theme'
import { USDFormatter } from 'utils/number/currency'
import { formatPercentage } from 'utils/number/percentage'

import { BLUE_THEME, product } from './shared'

export const holdingMapValue = (
  holding: PortfolioHolding | null,
  selectedView: string,
  formatted = false,
) => {
  if (holding) {
    switch (selectedView) {
      case 'dollars':
        return formatted ? USDFormatter.format(holding.amount) : holding.amount
      case 'quantity':
        return formatted
          ? parseFloat(holding.shares as any).toFixed(4)
          : holding.shares
      default:
        return formatted
          ? formatPercentage(holding.percentage / 100, 2)
          : holding.percentage
    }
  }
  return
}

const getChartData = (graphData: any, selectedView: string) => {
  return (
    typeof graphData[0] === 'object'
      ? getHoldingData(graphData, selectedView)
      : graphData
  ) as number[]
}

const getHoldingData = (graphData: any[] = [], selectedView = 'percentage') => {
  return graphData.map((item: any[]) => {
    return item.map((holding: PortfolioHolding) =>
      holdingMapValue(holding, selectedView),
    )
  })
}

type Props = {
  portfolio: Portfolio | SelfDirectedPortfolio | undefined
  graphData?: (Maybe<number> | undefined)[]
  selectedView?: string
  setSelectedEtf?: (value: number | null) => void
  portfolioType?: PortfolioType
  product?: string
}

export const getChartArcColors = (
  portfolio: Portfolio | SelfDirectedPortfolio | undefined,
  portfolioType?: PortfolioType,
  selfDirectedCustomTheme?: string[],
) => {
  if (portfolio) {
    // TODO: Remove conditional once we confirm that we can use portfolioSecurityAllocations in place of etfs
    if (portfolioType === undefined) {
      return (
        (portfolio as Portfolio).portfolioSecurityAllocations?.map(
          (security: PortfolioSecurityAllocation) => {
            if (security?.investmentCategories?.bond === 100) {
              return themeValues.colors.green
            } else if (security?.investmentCategories?.crypto === 100) {
              return CRYPTO_GRADIENT
            } else {
              return themeValues.colors.greenDark
            }
          },
        ) ?? []
      )
    } else if (portfolioType === PortfolioType.MANAGED) {
      return (
        portfolio.portfolioSecurityAllocations?.map(
          (security: PortfolioSecurityAllocation) => {
            if (security?.investmentCategories?.bond === 100) {
              return themeValues.colors.green
            } else if (security?.investmentCategories?.crypto === 100) {
              return CRYPTO_GRADIENT
            } else {
              return themeValues.colors.greenDark
            }
          },
        ) ?? []
      )
    }

    const selfDirectedTheme = selfDirectedCustomTheme ?? BLUE_THEME

    // Self Directed portfolio type.
    return (
      (portfolio as SelfDirectedPortfolio)?.portfolioSecurityAllocations?.map(
        () => selfDirectedTheme,
      ) ?? []
    )
  }

  return []
}

export const usePortfolioContent = ({
  portfolioType,
  graphData = [],
  selectedView = '',
  portfolio,
  setSelectedEtf,
  product: currentProduct,
}: Props) => {
  const [selectedDataIndex, setSelectedDataIndex] = useState<number | null>(
    null,
  )
  const location = useLocation()

  const chartData = useMemo(() => {
    return getChartData(graphData, selectedView)
  }, [graphData, selectedView])

  const securityPercentages = useMemo(() => {
    return getInvestmentCategoryPercentages(
      portfolio?.portfolioSecurityAllocations ?? [],
    )
  }, [portfolio?.portfolioSecurityAllocations])

  const handleSelectedDataChange = (_: any, elements: any) => {
    if (setSelectedEtf) {
      setSelectedEtf(elements[0]?._index ?? null)
    } else {
      setSelectedDataIndex(elements[0]?._index ?? null)
    }
  }

  const chartArcColors = getChartArcColors(portfolio, portfolioType)

  return {
    selectedDataIndex,
    totalBondsPercentage: securityPercentages.bondEtfPercent,
    totalStocksPercentage: securityPercentages.stockEtfPercent,
    totalCryptoPercentage:
      securityPercentages.cryptoEtfPercent !== '0%'
        ? securityPercentages.cryptoEtfPercent
        : null,
    handleSelectedDataChange,
    chartData,
    chartArcColors,
    product: product(location.pathname, currentProduct),
  }
}
